import { motion } from 'framer-motion';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { SizeProps } from '@/types/ui';
import Flex, { Center } from './Flex';
import SliderPage, { Props as SliderPageProps } from './SliderPage';
import { IconArrowLeft, IconArrowRight } from './icons';

interface StyleProps {
  width?: SizeProps;
}

export interface Props extends SliderPageProps, StyleProps {
  onPrevClick?: () => void;
  onNextClick?: () => void;
}

const Root = styled.div``;
const Button = styled.button`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  ${({ theme }) => theme.border[1]}
  ${({ theme }) => theme.rounded.full}
`;

export default function Carousel({
  value,
  width,
  items,
  onPrevClick,
  onNextClick,
  ...props
}: Props) {
  // state
  const [innerValue, setInnerValue] = useState(0);

  // memo
  const isUsingProps = useMemo(() => value !== undefined, [value]);
  const usingValue = useMemo(
    () => (isUsingProps ? value : innerValue),
    [innerValue, value, isUsingProps]
  );

  // callback
  const handlePrevClick = useCallback(
    () =>
      (
        onPrevClick ??
        (() =>
          setInnerValue((value) =>
            value <= 0 ? (items?.length ?? 0) - 1 : value - 1
          ))
      )(),
    [items?.length, onPrevClick]
  );
  const handleNextClick = useCallback(
    () =>
      (
        onNextClick ??
        (() =>
          setInnerValue((value) =>
            value >= (items?.length ?? 0) - 1 ? 0 : value + 1
          ))
      )(),
    [items?.length, onNextClick]
  );

  return (
    <Root>
      <Flex
        layout="size"
        as={motion.div}
        $direction="column"
        $width={width}
        $gap="xl"
      >
        <SliderPage value={usingValue} items={items} {...props} />
        <Center $gap="xl">
          <Button onClick={handlePrevClick}>
            <IconArrowLeft />
          </Button>
          <Button onClick={handleNextClick}>
            <IconArrowRight />
          </Button>
        </Center>
      </Flex>
    </Root>
  );
}
